<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
]);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};
</script>

<template>
  <div class="relative">
    <div
      class="grid items-center w-full gap-3 px-5 py-3 transition-colors duration-100 grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2.4fr)_minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_92px]"
      :class="
        isSelected ? 'bg-n-slate-3 dark:bg-n-solid-3' : 'hover:bg-n-slate-2'
      "
    >
      <div class="flex items-center min-w-0 gap-3">
        <div
          class="relative flex-shrink-0"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="36"
            :status="availabilityStatus"
            hide-offline-status
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px] border border-n-weak"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex flex-col min-w-0">
          <span class="text-[13.5px] font-semibold truncate text-n-slate-12">
            {{ name }}
          </span>
          <span
            v-if="additionalAttributes?.companyName"
            class="inline-flex items-center gap-1 text-xs truncate text-n-slate-10"
          >
            <span class="i-ph-building-light size-3.5 flex-shrink-0" />
            <span class="truncate">
              {{ additionalAttributes.companyName }}
            </span>
          </span>
          <span
            v-if="email || phoneNumber"
            class="text-xs truncate text-n-slate-10 md:hidden"
          >
            {{ [email, phoneNumber].filter(Boolean).join(' · ') }}
          </span>
        </div>
      </div>
      <span
        class="hidden md:block text-[13px] truncate text-n-slate-11"
        :title="email"
      >
        {{ email }}
      </span>
      <span class="hidden md:block text-[13px] truncate text-n-slate-11">
        {{ phoneNumber }}
      </span>
      <span
        class="items-center hidden min-w-0 gap-1.5 md:flex text-[13px] text-n-slate-11"
      >
        <Flag
          v-if="countryDetails"
          :country="countryDetails.countryCode"
          class="size-3.5 flex-shrink-0"
        />
        <span class="truncate">{{ formattedLocation }}</span>
      </span>
      <div class="flex items-center justify-end gap-1">
        <Button
          v-tooltip.top="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
          :aria-label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
          icon="i-lucide-arrow-up-right"
          variant="ghost"
          color="slate"
          size="xs"
          class="rounded-lg !text-n-slate-11"
          @click="onClickViewDetails"
        />
        <Button
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          class="rounded-lg !text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': isExpanded }"
          @click="onClickExpand"
        />
      </div>
    </div>

    <div
      class="transition-all duration-500 ease-in-out grid overflow-hidden"
      :class="
        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      "
    >
      <div class="overflow-hidden">
        <div class="flex flex-col gap-6 p-6 border-t border-n-slate-3">
          <ContactsForm
            ref="contactsFormRef"
            :contact-data="contactData"
            @update="handleFormUpdate"
          />
          <div>
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
              size="sm"
              :is-loading="isUpdating"
              :disabled="isUpdating || isFormInvalid"
              @click="handleUpdateContact"
            />
          </div>
        </div>
        <ContactDeleteSection
          :selected-contact="{
            id: props.id,
            name: props.name,
          }"
        />
      </div>
    </div>
  </div>
</template>
